import { TOCSelectorPageItem } from '@deckManagement/types/TableOfContentsSelector.types';
import { createId } from '@paralleldrive/cuid2';
import { findIndex, forEach, isEqual, isNil, some } from 'lodash';
import { CommonEntityMetadata } from 'types/Common.types';
import { EnhancedTOCEntry } from 'types/TOCContext.types';

export const DECK_PK_PREFIX = 'DECK';
export const PAGE_PK_PREFIX = 'PAGE';

/**
 * Converts an Entity ID string (e.g. `"deck-1"`, `"page-1"`, etc.) into a PK value for backend services (`"DECK#deck-1"`, `"PAGE#page-1"`)
 * @param id - The Entity ID to convert
 * @param pkPrefix - The prefix to use for the PK value (e.g. `"DECK"`, `"PAGE"`, etc.)
 */
export const entityIdAsKey = (id: string, pkPrefix: string): string => [pkPrefix, id].join('#');

/**
 * Converts a Deck PK value (`"DECK#deck-1"`, `"PAGE#page-1"`, etc.) into an Entity ID for frontend use (`"deck-1"`, `"page-1"`, etc.)
 * @param pk The Primary Key value from the backend services.
 */
export const entityKeyAsId = (pk: string): string => pk.split('#', 2)[1];

/**
 * Get the index of an Entity Item in the currently available list of Entity entries by its internalId.
 * @param currEntityEntries - The current list of Entity entries
 * @param {string} lookupId - The lookup internalId of the Entity item to find
 * @returns The index of the Entity item in the list, or -1 if not found
 */
export const getItemIdxByInternalId = <TEntity extends CommonEntityMetadata>(
  currEntityEntries: TEntity[],
  lookupId: string,
): number => {
  return findIndex(currEntityEntries, (itemEntry) => isEqual(itemEntry.metadata.internalId, lookupId));
};

/**
 * Calculates the next serial number for a new Entity entry
 * - The Entity is expected to have a serial_no property (e.g. TOCEntry, Deckline, etc.)
 *
 * Finds the maximum serial number from Entity entries
 * and returns the next sequential number. If no entries exist/entries
 * have null/undefined serial numbers, it returns 1.
 *
 * @param entries - Array of existing Entity entries
 * @returns The next available serial number (minimum value is 1)
 *
 */
export const getNextSerialNumber = <TEntity extends { serial_no?: string | number | null }>(
  entries: TEntity[],
): number => {
  if (!entries || entries.length === 0) return 1;

  const serialNumbers = entries.map((entry) => {
    const serialNo = entry?.serial_no;
    if (isNil(serialNo)) return 0;
    return typeof serialNo === 'string' ? parseFloat(serialNo) : serialNo;
  });
  const maxSerialNo = serialNumbers.length > 0 ? Math.max(...serialNumbers) : 0;
  return maxSerialNo + 1;
};

/**
 * Generates a list of selector items for use in views that support a side Table of Contents navigation dropdown menu
 * @param deckId - The Deck ID for which the selector items are being generated
 * @param tocEntries - Array of TOC entries to be converted into selector items
 * @returns A list of TOC selector items
 */
export const generateTocSelectorPageItems = (deckId: string, tocEntries: EnhancedTOCEntry[]) => {
  const pageItems: Array<TOCSelectorPageItem> = [];

  forEach(tocEntries, (tocEntry) => {
    const newTocPageItem: TOCSelectorPageItem = {
      pageId: tocEntry.page_id || '',
      pageNumber: tocEntry.page_no || '',
      serialNo: tocEntry.serial_no || 0,
      pageName: tocEntry.page_name || '',
    };

    pageItems.push(newTocPageItem);
  });

  return pageItems;
};

/**
 * Generates a new entity metadata object to apply to entity items as needed
 * @param nextSerialNo - The next serial number to use for the new entity (optional, for use in entities with row dragging)
 * @returns A new entity metadata object with a unique cuid2 that is collision-resistant and date sortable
 */
export const generateEntityMetadata = (nextSerialNo?: number) => {
  const newBaseMetadata: CommonEntityMetadata<unknown> = {
    metadata: {
      internalId: createId(),
      isNewRow: false,
      isDeleted: false,
      isModified: false,
      sortPos: nextSerialNo,
    },
  };

  return newBaseMetadata;
};

/**
 * Parses the ID from the sort key containing #
 * @param sk - The sort key of the entity
 * @returns The ID part
 */
export const parseIdFromKey = (sk: string) => {
  return sk.split('#')[1];
};

/**
 * Checks if a string field value already exists in a collection.
 * Specifically relevant for checks in collections such as ScenarioManagement and EnhancedTOCEntry collections.
 * Used in preventing duplicates where not allowed, such as for Page Name in a TOC Entry
 * @param collection - The collection to check against
 * @param checkField - The field(s) to check against
 * @param checkValue - The value(s) to check for
 * @param excludeEntryItem - Optional item that we should exclude from the check (to avoid false positives against this item)
 * @returns True if a duplicate entry is found, false otherwise
 */
export const checkDuplicateFieldEntry = <TEntryItem extends object, TField extends keyof TEntryItem>(
  collection: TEntryItem[],
  checkField: TField | TField[],
  checkValue: TEntryItem[TField] | TEntryItem[TField][],
  excludeEntryItem?: TEntryItem,
) => {
  const CHECK_DELIMITER = '#';

  const checkFields = Array.isArray(checkField) ? checkField : [checkField];
  const checkValues = Array.isArray(checkValue) ? checkValue : [checkValue];

  const checkComposite = checkValues.join(CHECK_DELIMITER);

  // Do comparison check in the item collection
  return some(collection, (item) => {
    if (item === excludeEntryItem) return false;
    const itemComposite = checkFields.map((field) => item[field]).join(CHECK_DELIMITER);
    return itemComposite === checkComposite;
  });
};
